import { useState, type ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/toast';
import { ApiError, apiErrorReason } from '@/lib/api';

import {
  CLOSE_STALE_REASONS,
  closeTrainingErrorText,
  closeTrainingToast,
} from '../training-stage-presentation';
import { useCloseTraining } from '../use-close-training';

const REASON = {
  label: 'Motivo del cierre',
  min: 10,
  max: 500,
  placeholder: 'Por ejemplo: el documento se borró en el LMS y no se volverá a cargar',
};

interface Props {
  entryId: string;
  code: string;
  /** La etapa cambió en el servidor (otro cierre ganó): recargar la tarjeta. */
  onStale: () => void;
}

/** Cierre de la etapa de capacitación por Calidad cuando el LMS no reporta el documento
 *  (G2-C, A-05): ConfirmModal con motivo obligatorio, toast específico y error accionable. */
export function CloseTrainingAction({ entryId, code, onStale }: Props): ReactElement {
  const close = useCloseTraining();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dismiss = (): void => {
    setOpen(false);
    setError(null);
  };
  const confirm = (reason?: string): void => {
    setError(null);
    close.mutate(
      { entryId, reason: reason ?? '' },
      {
        onSuccess: () => {
          dismiss();
          showToast(closeTrainingToast(code));
        },
        onError: (err) => {
          const why = apiErrorReason(err);
          const text = closeTrainingErrorText(
            why,
            err instanceof ApiError ? err.status : undefined,
          );
          // La etapa cambió en el servidor: la tarjeta se recarga y este botón desaparece,
          // así que el aviso va en un toast (un alerta dentro del modal se perdería).
          if (why !== undefined && CLOSE_STALE_REASONS.has(why)) {
            dismiss();
            showToast(text, 'error');
            onStale();
            return;
          }
          setError(text);
        },
      },
    );
  };

  return (
    <>
      <button type="button" className="btn mut sm fld-gap" onClick={() => setOpen(true)}>
        Cerrar etapa
      </button>
      <ConfirmModal
        open={open}
        title="Cerrar la etapa de capacitación"
        message={`${code} deja de esperar el reporte del LMS. La etapa queda como cerrada por Calidad, con el motivo indicado.`}
        confirmLabel="Cerrar etapa"
        destructive
        busy={close.isPending}
        reason={REASON}
        error={error}
        onConfirm={confirm}
        onCancel={dismiss}
      />
    </>
  );
}
